import React from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import '../styles/components/_login-language-switcher.scss';
import {
  DEFAULT_LOGIN_PICKER_LANGUAGES,
  normalizeLanguageList,
} from '../utils/loginLanguages';
import { getLanguageAriaLabel, getLanguageFlagSrc } from '../utils/languageFlags';

interface LoginLanguageSwitcherProps {
  /** Langues proposées. Si vide ou une seule, le composant ne s'affiche pas. */
  languages?: string[];
  /** Langue sélectionnée (formation ou UI). */
  selectedLanguage?: string;
  onChange?: (lang: string) => void;
  /** Contrôle l'animation d'apparition (SCORM : attendre le chargement). */
  visible?: boolean;
}

const LoginLanguageSwitcher: React.FC<LoginLanguageSwitcherProps> = ({
  languages = [...DEFAULT_LOGIN_PICKER_LANGUAGES],
  selectedLanguage,
  onChange,
  visible = true,
}) => {
  const { i18n, t } = useTranslation();
  const activeLanguage = (
    selectedLanguage || i18n.language || 'fr'
  )
    .split('-')[0]
    .toLowerCase();

  const normalizedLanguages = normalizeLanguageList(languages);

  if (normalizedLanguages.length <= 1) {
    return null;
  }

  const handleChange = (_event: React.MouseEvent<HTMLElement>, value: string | null) => {
    if (value && value !== activeLanguage) {
      onChange?.(value);
    }
  };

  return (
    <ToggleButtonGroup
      className={`login-language-switcher${visible ? ' login-language-switcher--visible' : ''}`}
      value={activeLanguage}
      exclusive
      onChange={handleChange}
      aria-label={t('common.language')}
      aria-hidden={!visible}
      size="small"
    >
      {normalizedLanguages.map((lang) => (
        <ToggleButton
          key={lang}
          value={lang}
          className="login-language-switcher__toggle"
          aria-label={getLanguageAriaLabel(lang, t)}
        >
          <img
            src={getLanguageFlagSrc(lang)}
            alt=""
            className="login-language-switcher__flag"
            width={28}
            height={20}
            loading="lazy"
            decoding="async"
          />
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

export default LoginLanguageSwitcher;
